import React from 'react';

import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';
import RemoveFromWorklistModal from 'worklists/RemoveFromWorklistModal';
import {RefreshOptions} from 'worklists/WorklistActions';
import {mixedWorklistTypeError} from 'worklists/WorklistConstants';
import {allIdsSupported, apiIdsHaveSameWorklistType, areIdsValidForWorklist} from 'worklists/WorklistUtil';

import {WorklistSummaryGraphQL} from '../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';

export interface RemoveFromWorklistButtonProps {
  className?: string;
  disabled: boolean;
  ids: Array<string>;
  worklist: WorklistSummaryGraphQL;
  refreshOptions: RefreshOptions;
  // This callback aims to track user clicks.
  onClickTracking?: () => void;
}

/**
 * Button to remove items from a worklist. Intended to be used from within a worklist sidepanel view.
 */
export default function RemoveFromWorklistButton(props: RemoveFromWorklistButtonProps): JSX.Element {
  const idsValid = areIdsValidForWorklist(props.ids);
  const disabled = props.disabled || !idsValid;

  function getTooltipTitle(): string {
    if (idsValid) {
      return 'Remove items from worklist';
    } else if (props.ids.length === 0) {
      return 'Must select items';
    } else if (!allIdsSupported(props.ids)) {
      return 'Some selected items cannot be removed from worklists';
    } else if (!apiIdsHaveSameWorklistType(props.ids)) {
      return mixedWorklistTypeError;
    } else {
      throw new Error(`Must add error message for invalid worklist item ids: [${props.ids.join(', ')}]`);
    }
  }

  return (
    <Tooltip title={getTooltipTitle()}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        className={props.className}
        disabled={disabled}
        icon="delete"
        onClick={() => {
          props.onClickTracking?.();
          legacyRunModal(RemoveFromWorklistModal, {
            ids: props.ids,
            worklist: props.worklist,
            refreshOptions: props.refreshOptions,
          });
        }}
        size={ButtonSize.SMALL}
      />
    </Tooltip>
  );
}
